// Top-level wrapper used only when the React app is running inside the
// Electron desktop companion. Renders one of two things:
//
//   - Collapsed: just the floating R + red pending-count badge.
//   - Expanded: a slim drag-handle titlebar + the full existing React
//                app (children). RequestsPage handles its own polling
//                once mounted, so we deliberately *unmount* children
//                when collapsed (no double-poll, no stale state).
//
// The collapse/expand toggle is mirrored to the Electron main process
// via the IPC bridge (window.electronAPI.setExpanded) so the BrowserWindow
// resizes between 80×80 and 420×720.

import React, { useEffect, useState } from "react";

import { FloatingButton } from "./FloatingButton";
import { usePendingCount } from "./usePendingCount";
import "./desktop.css";
import { t } from "../i18n";

// Make TypeScript aware of the bridge exposed in preload.js.
declare global {
  interface Window {
    electronAPI?: {
      setExpanded: (expanded: boolean) => Promise<void>;
      quitApp: () => Promise<void>;
      confirmQuit: () => Promise<boolean>;
      dragWindowBy: (dx: number, dy: number) => void;
      getEnvInfo: () => Promise<{
        isDev: boolean;
        platform: string;
        baseUrl: string;
      }>;
      isElectron: boolean;
      platform: string;
    };
  }
}

interface Props {
  children: React.ReactNode;
}

const STORAGE_KEY = "requestapp.desktopExpanded";

export const DesktopShell: React.FC<Props> = ({ children }) => {
  // Persist last open/closed state so a quit + relaunch reopens to the
  // same mode. New users default to collapsed (the whole point of the
  // overlay is to start out of the way).
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  // usePendingCount polls continuously now (whether the panel is open
  // or not). This guarantees the badge is correct the instant the
  // mini R reappears on collapse — previously we gated it on
  // `!expanded`, which meant a fresh post-collapse fetch had to land
  // before the badge could show, producing a "no badge after
  // minimize" UX bug.
  const { count, isOnline, pulseTrigger } = usePendingCount();

  // Tell Electron to resize the window whenever expanded state flips.
  // Also persist for next launch.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, expanded ? "1" : "0");
    } catch {
      /* storage disabled — non-fatal */
    }
    window.electronAPI?.setExpanded(expanded).catch(() => {
      /* main process not up yet — non-fatal */
    });
  }, [expanded]);

  const handleExpand = () => setExpanded(true);
  const handleCollapse = () => setExpanded(false);

  // "Remove from screen" → ask the DJ to confirm via a native macOS
  // dialog, then fully quit the Electron app. Both the mini R and the
  // expanded panel disappear. The DJ re-launches via Spotlight (Cmd+
  // Space → "Request App") or the dock when packaged.
  const handleClose = async () => {
    try {
      const confirmed = await window.electronAPI?.confirmQuit();
      if (confirmed) {
        await window.electronAPI?.quitApp();
      }
    } catch {
      /* not in Electron, or main process unavailable — no-op */
    }
  };

  if (!expanded) {
    return (
      <FloatingButton
        count={count}
        isOnline={isOnline}
        pulseTrigger={pulseTrigger}
        onExpand={handleExpand}
      />
    );
  }

  return (
    <div className="desktop-expanded-root">
      <div className="desktop-titlebar">
        <span className="desktop-titlebar-title">{t("desktopShell.request")}</span>
        <div className="desktop-titlebar-actions">
          <button
            className="desktop-titlebar-close"
            onClick={handleClose}
            aria-label={t("desktopShell.quitRequestApp")}
            title={t("desktopShell.quitRemoveFromScreen")}
          >
            <span aria-hidden>×</span>
          </button>
          <button
            className="desktop-titlebar-collapse"
            onClick={handleCollapse}
            aria-label={t("desktopShell.collapseToFloatingButton")}
            title={t("desktopShell.minimizeToMiniR")}
          >
            <span aria-hidden>−</span>
          </button>
        </div>
      </div>
      <div className="desktop-app-frame">{children}</div>
    </div>
  );
};
